import cx from "classnames";
import capitalize from "lodash/capitalize";
import React, { useCallback, useState } from "react";
import { Box } from "@novel/shared/components/Box";
import { FixedDimensionMedia } from "@novel/shared/components/FixedDimensionMedia";
import { NovelModal } from "@novel/shared/components/NovelModal";
import { SubduedText } from "@novel/shared/components/Primitives";
import { useIsMobile } from "@novel/shared/hooks/useIsMobile";
import { ICryptoAddressWithTokensAndWallets } from "@novel/shared/interfaces/CryptoAddress";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { useAsyncCallback } from "@novel/shared/utils/useAsyncCallback";
import { WalletIcon } from "@novel/shared/components/ConnectWalletComponent/WalletIcon";
import { claimTokenLineItem } from "@customer-gallery/redux/actionCreators/unclaimedTokens";
import { useNovelCustomerDispatch, useNovelCustomerSelector } from "@customer-gallery/redux/reduxHooks";
import { useWallets } from "@customer-gallery/redux/reducers/cryptoAddresses";
import { GalleryAuthWallet } from "@customer-gallery/utils/galleryWallets";
import { errorToast } from "@novel/shared/utils/toastUtils";
interface IConnectWalletModalProps {
  readonly claimingTokenLineItem: ITokenCollectionClaimWithTokenCollection;
  readonly onClose: () => void;
}
export function ClaimModal({
  claimingTokenLineItem,
  onClose
}: IConnectWalletModalProps): JSX.Element {
  const isMobile = useIsMobile();
  const [modalOpen, setModalOpen] = useState(true);
  const wallets = useWallets({
    evmOnly: true
  });
  const isClaimingToken = useNovelCustomerSelector(state => state.unclaimedTokens.isClaimingToken);

  // delaying to allow animation out
  const finalHandleOnClose = useCallback(() => {
    setModalOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);
  const dispatch = useNovelCustomerDispatch();
  const claimTokensWithWallet = useAsyncCallback(async (address: ICryptoAddressWithTokensAndWallets, wallet: ICryptoWallet) => {
    if (claimingTokenLineItem.collectionToMintFrom.collectionMetadata.decentralizedMintEnabled && wallet.connectionApp === "wallet-connect-v2") {
      return errorToast({
        content: "Wallet V2 doesn't support decentralized mint yet!"
      });
    } else if (claimingTokenLineItem.collectionToMintFrom.collectionMetadata.decentralizedMintEnabled) {
      await new GalleryAuthWallet(wallet).ensureWalletAppInstalled(async () => {
        finalHandleOnClose();
        await dispatch(claimTokenLineItem(address, wallet, claimingTokenLineItem));
      });
    } else {
      finalHandleOnClose();
      await dispatch(claimTokenLineItem(address, wallet, claimingTokenLineItem));
    }
  }, [dispatch, claimingTokenLineItem, finalHandleOnClose]);
  return <React.Fragment>
            <NovelModal dialogClassName="claiming-nft-modal" className="claiming-nft-modal" size="md" open={modalOpen} onClose={finalHandleOnClose}>
                <NovelModal.Header />
                <NovelModal.Body>
                    <Box alignItems="stretch" columnGap={4} rowGap={4} mobileDirection="column" mobileAlignItems="center">
                        <Box minWidth={160}>
                            <FixedDimensionMedia className="claiming-modal-nft-image" size="larger" src={claimingTokenLineItem.collectionToMintFrom.collectionMetadata.cloudImage || (claimingTokenLineItem.collectionToMintFrom.collectionMetadata.ipfsImageCid ? `${IPFS_GATEWAY_PREFIX}${claimingTokenLineItem.collectionToMintFrom.collectionMetadata.ipfsImageCid}` : "")} />
                        </Box>
                        <Box column flexGrow={1} rowGap={0.4} maxWidth="calc(100% - 160px - 2rem)" mobileMaxWidth="100%">
                            <SubduedText mb={0.2} mobileJustifyContent="center">
                                <span className="claiming-modal-claim-text">
                                    Claim your{" "}
                                    <u>
                                        {claimingTokenLineItem.collectionToMintFrom.collectionMetadata.title}
                                    </u>{" "}
                                    token!
                                </span>
                            </SubduedText>
                            <SubduedText>
                                {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                                <React.Fragment />
                            </SubduedText>
                            <Box justifyContent="flex-end" alignItems="stretch" mt={0.2} flexGrow={1} column>
                                <Box mb={1.4} mobileJustifyContent="center">
                                    <b>
                                        Select which wallet you'd like to claim these token(s) with
                                        {isMobile ? "" : ":"}
                                    </b>
                                </Box>
                                <Box columnGap={1.4} rowGap={1.4} flexWrap="wrap" mobileJustifyContent="center">
                                    {wallets.flatMap(wallet => wallet.addresses.map(address => ({
                  address,
                  cryptoWallet: wallet.cryptoWallet
                }))).map(({
                  address,
                  cryptoWallet
                }) => <WalletSelectionCmp key={`${address.id}_${cryptoWallet.id}`} address={address} wallet={cryptoWallet} onClick={!isClaimingToken && claimTokensWithWallet || undefined} />)}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </NovelModal.Body>
            </NovelModal>
            <style global jsx>{`
                .claiming-modal-wallet-list-wallet {
                    display: flex;
                    position: relative;
                    padding: 10px;
                    border-radius: 6px;
                    cursor: default;

                    box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
                    transition:
                        transform 0.15s ease-in-out,
                        background-color 0.15s ease-in-out;
                }

                .claiming-nft-modal .rs-modal-content {
                    border-radius: 8px !important;
                }

                .claiming-nft-modal {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .claiming-nft-modal .rs-modal-content .rs-modal-body {
                    overflow: auto !important;
                }

                .claiming-modal-nft-images {
                    border-radius: 32px !important;
                }

                .claiming-modal-wallet-list-wallet-content-wrapper {
                    transition:
                        opacity 0.15s ease-in-out,
                        filter 0.3s ease-in-out;
                    overflow: hidden;
                }
                .claiming-modal-wallet-list-wallet-content-wrapper-disabled {
                    filter: blur(4px);
                    -webkit-filter: blur(4px);
                    opacity: 0.5;
                }

                .claim-modal-wallet-claiming-message {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-weight: bold;
                }

                .claiming-modal-wallet-list-wallet-text-wrapper {
                    position: relative;
                    overflow: hidden;
                    flex-grow: 1;
                }

                .claiming-modal-wallet-list-text {
                    display: inline-block;
                    position: relative;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 1.12;
                    max-width: 100%;
                }

                .claiming-modal-wallet-list-wallet:not(
                        .claiming-modal-wallet-list-wallet-disabled
                    ) {
                    cursor: pointer;
                }

                .claiming-modal-wallet-list-wallet:hover:not(
                        .claiming-modal-wallet-list-wallet-disabled
                    ) {
                    transform: scale(1.08);
                    background-color: #f5f5f5;
                }

                .claiming-modal-nft-image {
                    width: 100%;
                    border-radius: 20px;
                    overflow: hidden;
                }

                .claiming-modal-claim-text {
                    font-size: 2.4rem;
                    font-weight: 700;
                }

                .claim-modal-wallet-icon {
                    height: 62px;
                    width: 62px !important;
                    min-width: 62px;
                    min-height: 62px;
                }

                @media (max-width: 768px) {
                    .claiming-nft-modal .rs-modal-content {
                        height: 100vh;
                    }
                }
            `}</style>
        </React.Fragment>;
}
interface WalletSelectionCmpProps {
  readonly address: ICryptoAddressWithTokensAndWallets;
  readonly wallet: ICryptoWallet;
  readonly onClick?: (address: ICryptoAddressWithTokensAndWallets, wallet: ICryptoWallet) => void;
}
function WalletSelectionCmp({
  address,
  wallet,
  onClick
}: WalletSelectionCmpProps) {
  const onWalletClick = useCallback(() => {
    if (onClick) {
      onClick(address, wallet);
    }
  }, [address, wallet, onClick]);
  return <Box key={address.id} className={cx("claiming-modal-wallet-list-wallet", {
    "claiming-modal-wallet-list-wallet-disabled": !onClick
  })} flexGrow={1} minWidth={120} maxWidth="calc(50% - 1.4rem - 6px)" mobileMaxWidth="89%" onClick={onWalletClick}>
            <Box flexGrow={1} columnGap={1} rowGap={1} className={cx("claiming-modal-wallet-list-wallet-content-wrapper", {
      "claiming-modal-wallet-list-wallet-content-wrapper-disabled": !onClick
    })}>
                <WalletIcon className="claim-modal-wallet-icon" walletConnectionApp={wallet.connectionApp} />
                <Box column className="claiming-modal-wallet-list-wallet-text-wrapper" rowGap={0.1}>
                    <SubduedText className="claiming-modal-wallet-list-text">
                        {address.address}
                    </SubduedText>
                    {wallet.verifier && <SubduedText className="claiming-modal-wallet-list-text">
                            {capitalize(wallet.verifier)}
                        </SubduedText>}
                    {wallet.verifierId && <SubduedText className="claiming-modal-wallet-list-text">
                            {wallet.verifierId}
                        </SubduedText>}
                </Box>
            </Box>
            {!onClick && <div className="claim-modal-wallet-claiming-message">Claiming token...</div>}
        </Box>;
}